import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  VendorSmsConfig,
  SmsTemplate,
  LegacySmsTemplateDef,
  ProductRuleLike,
  loadConfig,
  saveConfig,
  emptyConfig,
  newTemplateId,
} from './smsConfig';

const SMS_STORAGE_PREFIX = 'vendor-sms-config:';

function authHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionStorage.getItem('admin-token') || ''}` };
}

interface RecentOption {
  optionCode: string;
  optionInfo: string;
  productName: string;
}

interface Props {
  vendorCode: string;
  vendorName: string;
  legacyTemplates: LegacySmsTemplateDef[];
}

export default function SmsTemplateTab({ vendorCode, vendorName, legacyTemplates }: Props) {
  const [config, setConfig] = useState<VendorSmsConfig>(() => loadConfig(vendorCode) ?? emptyConfig());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftLabel, setDraftLabel] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [copiedId, setCopiedId] = useState('');
  const [toast, setToast] = useState('');
  const [rules, setRules] = useState<ProductRuleLike[]>([]);
  const [recentOptions, setRecentOptions] = useState<RecentOption[]>([]);
  const [selectedRuleIds, setSelectedRuleIds] = useState<Set<string>>(new Set());
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const importFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setRules([]);
    fetch(`/api/product-rules?vendor=${encodeURIComponent(vendorCode)}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => { if (!cancelled) setRules(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setRules([]); });
    return () => { cancelled = true; };
  }, [vendorCode]);

  useEffect(() => {
    let cancelled = false;
    setRecentOptions([]);
    fetch(`/api/vendor/recent-option-names?vendorCode=${encodeURIComponent(vendorCode)}&limit=500`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => { if (!cancelled) setRecentOptions(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setRecentOptions([]); });
    return () => { cancelled = true; };
  }, [vendorCode]);

  const persist = (next: VendorSmsConfig) => {
    setConfig(next);
    saveConfig(vendorCode, next);
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const startAdd = () => {
    setEditingId(null);
    setAddingNew(true);
    setDraftLabel('');
    setDraftBody('');
  };

  const startEdit = (t: SmsTemplate) => {
    setAddingNew(false);
    setEditingId(t.id);
    setDraftLabel(t.label);
    setDraftBody(t.body);
  };

  const cancelEdit = () => {
    setAddingNew(false);
    setEditingId(null);
  };

  const saveDraft = () => {
    const label = draftLabel.trim();
    const body = draftBody;
    if (!label || !body.trim()) {
      alert('이름과 본문을 모두 입력해주세요');
      return;
    }
    if (addingNew) {
      const t: SmsTemplate = { id: newTemplateId(), label, body };
      persist({ ...config, templates: [...config.templates, t] });
      showToast('템플릿이 추가되었습니다');
    } else if (editingId) {
      persist({
        ...config,
        templates: config.templates.map(t => t.id === editingId ? { ...t, label, body } : t),
      });
      showToast('템플릿이 수정되었습니다');
    }
    setAddingNew(false);
    setEditingId(null);
  };

  const deleteTemplate = (id: string) => {
    if (!window.confirm('이 템플릿을 삭제할까요? 관련 매핑도 해제됩니다.')) return;
    const nextAssignments: Record<string, string | null> = {};
    for (const [k, v] of Object.entries(config.assignments)) {
      nextAssignments[k] = v === id ? null : v;
    }
    persist({
      ...config,
      templates: config.templates.filter(t => t.id !== id),
      assignments: nextAssignments,
      defaultTemplateId: config.defaultTemplateId === id ? null : config.defaultTemplateId,
    });
    showToast('템플릿이 삭제되었습니다');
  };

  const setDefault = (id: string) => {
    persist({ ...config, defaultTemplateId: config.defaultTemplateId === id ? null : id });
  };

  const assignTemplate = (ruleId: string, templateId: string) => {
    const nextValue = templateId || null;
    persist({ ...config, assignments: { ...config.assignments, [ruleId]: nextValue } });
  };

  const toggleRuleSelected = (ruleId: string) => {
    setSelectedRuleIds(prev => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId); else next.add(ruleId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedRuleIds(prev => prev.size === rules.length ? new Set() : new Set(rules.map(r => r.id)));
  };

  const applyBulk = () => {
    if (selectedRuleIds.size === 0) return;
    const nextValue = bulkTemplateId || null;
    const nextAssignments = { ...config.assignments };
    selectedRuleIds.forEach(id => { nextAssignments[id] = nextValue; });
    persist({ ...config, assignments: nextAssignments });
    showToast(`${selectedRuleIds.size}건에 템플릿을 적용했습니다`);
    setSelectedRuleIds(new Set());
  };

  const exportAllConfigs = () => {
    const out: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(SMS_STORAGE_PREFIX)) continue;
      const v = localStorage.getItem(k);
      if (v != null) out[k] = v;
    }
    const count = Object.keys(out).length;
    if (count === 0) {
      alert('이 브라우저에 백업할 배송문자 설정이 없습니다.');
      return;
    }
    const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.download = `vendor-sms-backup-${ts}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    showToast(`${count}개 업체 설정을 백업했습니다`);
  };

  const handleImportFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    let data: unknown;
    try {
      data = JSON.parse(await file.text());
    } catch {
      alert('백업 파일을 읽지 못했습니다. JSON 형식인지 확인해주세요.');
      return;
    }
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      alert('백업 파일 형식이 올바르지 않습니다.');
      return;
    }
    const entries = Object.entries(data as Record<string, unknown>)
      .filter(([k, v]) => k.startsWith(SMS_STORAGE_PREFIX) && typeof v === 'string') as [string, string][];
    if (entries.length === 0) {
      alert('백업 파일에 vendor-sms-config 항목이 없습니다.');
      return;
    }
    const validEntries: [string, string][] = [];
    for (const [k, v] of entries) {
      try {
        const parsed = JSON.parse(v);
        if (parsed && parsed.version === 1 && Array.isArray(parsed.templates) && parsed.assignments) {
          validEntries.push([k, v]);
        }
      } catch {
        // skip invalid
      }
    }
    if (validEntries.length === 0) {
      alert('백업 파일의 항목이 모두 유효하지 않습니다 (version=1 형식 아님).');
      return;
    }
    const ok = window.confirm(`${validEntries.length}개 업체 설정을 현재 브라우저에 복원합니다. 같은 업체의 기존 설정은 덮어쓰입니다. 진행할까요?`);
    if (!ok) return;
    for (const [k, v] of validEntries) localStorage.setItem(k, v);
    showToast(`${validEntries.length}개 업체 설정을 복원했습니다. 새로고침 중...`);
    setTimeout(() => window.location.reload(), 700);
  };

  const copy = async (body: string, id: string) => {
    try {
      await navigator.clipboard.writeText(body);
      setCopiedId(id);
      setTimeout(() => setCopiedId(''), 1500);
    } catch {
      alert('복사 실패 — 브라우저 권한을 확인해주세요');
    }
  };

  const usingLegacy = loadConfig(vendorCode) === null && legacyTemplates.length > 0;

  const unmatchedRecent = recentOptions.filter(ro =>
    !rules.some(r => {
      const keywords = r.source_keyword.split(',').map(k => k.trim()).filter(Boolean);
      return keywords.length > 0 && keywords.every(kw => ro.optionInfo.includes(kw));
    })
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2 text-xs">
        <span className="text-gray-400 mr-1">전체 업체 SMS 설정:</span>
        <button
          onClick={exportAllConfigs}
          className="px-3 py-1.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
        >백업 다운로드</button>
        <button
          onClick={() => importFileRef.current?.click()}
          className="px-3 py-1.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
        >백업에서 복원</button>
        <input
          ref={importFileRef}
          type="file"
          accept="application/json,.json"
          onChange={handleImportFile}
          className="hidden"
        />
      </div>

      {usingLegacy && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-xs text-yellow-800 leading-relaxed">
          현재 하드코딩된 기본 템플릿이 적용 중입니다. 아래에서 템플릿을 추가하면 이후로는 localStorage 설정이 사용됩니다.
          <details className="mt-2 cursor-pointer">
            <summary>하드코딩 템플릿 보기 ({legacyTemplates.length}개)</summary>
            <div className="mt-2 space-y-2">
              {legacyTemplates.map((t, i) => (
                <div key={i} className="bg-white border border-yellow-100 rounded p-2">
                  <div className="font-semibold">{t.label}</div>
                  <pre className="whitespace-pre-wrap text-gray-700 mt-1">{t.body}</pre>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">배송문자 템플릿</h3>
          <button onClick={startAdd} className="px-4 py-2 bg-jeju-brown text-white rounded-lg text-sm font-medium hover:bg-jeju-brown-dark transition-colors">+ 템플릿 추가</button>
        </div>

        {(addingNew || editingId) && (
          <div className="border border-jeju-brown/40 bg-jeju-brown/5 rounded-lg p-4 mb-4 space-y-2">
            <input
              value={draftLabel}
              onChange={e => setDraftLabel(e.target.value)}
              placeholder="템플릿 이름 (예: 유기농 당근)"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown"
            />
            <textarea
              value={draftBody}
              onChange={e => setDraftBody(e.target.value)}
              placeholder="문자 본문"
              className="w-full h-40 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown resize-none font-sans"
            />
            <div className="flex justify-end gap-2">
              <button onClick={cancelEdit} className="px-3 py-1.5 text-sm text-gray-500">취소</button>
              <button onClick={saveDraft} className="px-4 py-1.5 bg-jeju-brown text-white rounded text-sm font-medium">저장</button>
            </div>
          </div>
        )}

        {config.templates.length === 0 && !addingNew && (
          <p className="text-sm text-gray-400 text-center py-6">등록된 템플릿이 없습니다. "+ 템플릿 추가"를 눌러주세요.</p>
        )}

        <div className="space-y-3">
          {config.templates.map(t => (
            <div key={t.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-gray-800">{t.label}</h4>
                  {config.defaultTemplateId === t.id && (
                    <span className="text-[10px] bg-jeju-brown text-white px-2 py-0.5 rounded">기본</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setDefault(t.id)}
                    className={`text-xs px-2 py-1 rounded ${config.defaultTemplateId === t.id ? 'bg-gray-100 text-gray-500' : 'text-jeju-brown hover:bg-jeju-brown/10'}`}
                  >{config.defaultTemplateId === t.id ? '기본 해제' : '기본으로 설정'}</button>
                  <button onClick={() => copy(t.body, t.id)} className="text-xs px-2 py-1 text-gray-600 hover:bg-gray-100 rounded">{copiedId === t.id ? '복사됨' : '복사'}</button>
                  <button onClick={() => startEdit(t)} className="text-xs px-2 py-1 text-jeju-brown hover:bg-jeju-brown/10 rounded">수정</button>
                  <button onClick={() => deleteTemplate(t.id)} className="text-xs px-2 py-1 text-red-500 hover:bg-red-50 rounded">삭제</button>
                </div>
              </div>
              <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 rounded border border-gray-100 p-3 font-sans">{t.body}</pre>
              <p className="text-[11px] text-gray-400 mt-1">{vendorName} · {t.body.length}자</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">상품 옵션별 템플릿 매핑</h3>

        {rules.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">이 업체의 상품명 관리 규칙이 없습니다. 먼저 "상품명 관리" 탭에서 규칙을 등록해주세요.</p>
        ) : (
          <>
            {selectedRuleIds.size > 0 && (
              <div className="flex items-center gap-2 mb-3 p-3 bg-jeju-brown/5 border border-jeju-brown/20 rounded">
                <span className="text-sm text-gray-700">선택된 <strong>{selectedRuleIds.size}</strong>건에 적용:</span>
                <select
                  value={bulkTemplateId}
                  onChange={e => setBulkTemplateId(e.target.value)}
                  className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown"
                >
                  <option value="">미지정 (기본 템플릿 사용)</option>
                  {config.templates.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
                <button onClick={applyBulk} className="px-4 py-1.5 bg-jeju-brown text-white rounded text-sm font-medium hover:bg-jeju-brown-dark">적용</button>
                <button onClick={() => setSelectedRuleIds(new Set())} className="px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded">선택 해제</button>
              </div>
            )}
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 border-b border-gray-200 w-10">
                      <input
                        type="checkbox"
                        checked={rules.length > 0 && selectedRuleIds.size === rules.length}
                        ref={el => { if (el) el.indeterminate = selectedRuleIds.size > 0 && selectedRuleIds.size < rules.length; }}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-200">치환 상품명</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-200 w-64">템플릿</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rules.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedRuleIds.has(r.id)}
                          onChange={() => toggleRuleSelected(r.id)}
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-800">{r.target_name}</td>
                      <td className="px-3 py-2">
                        <select
                          value={config.assignments[r.id] ?? ''}
                          onChange={e => assignTemplate(r.id, e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown"
                        >
                          <option value="">미지정 (기본 템플릿 사용)</option>
                          {config.templates.map(t => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {unmatchedRecent.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-orange-800 mb-2">⚠ 최근 주문 중 규칙 미매칭 옵션</h3>
          <p className="text-xs text-orange-700 mb-3">아래 옵션은 상품명 관리 규칙에 매칭되지 않아 기본 템플릿이 전송됩니다. 별도 템플릿이 필요하면 "상품명 관리" 탭에서 규칙을 등록해주세요.</p>
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="text-left px-2 py-1.5 text-orange-700">옵션관리코드</th>
                <th className="text-left px-2 py-1.5 text-orange-700">옵션정보</th>
                <th className="text-left px-2 py-1.5 text-orange-700">상품명</th>
              </tr>
            </thead>
            <tbody>
              {unmatchedRecent.map(o => (
                <tr key={o.optionCode + '|' + o.optionInfo} className="border-t border-orange-100">
                  <td className="px-2 py-1 font-mono text-orange-900">{o.optionCode}</td>
                  <td className="px-2 py-1 text-orange-900">{o.optionInfo}</td>
                  <td className="px-2 py-1 text-orange-800">{o.productName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-jeju-brown text-white px-6 py-2.5 rounded-lg text-sm font-semibold z-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
